<template>
	<v-card class="race-element rounded-lg" @click="showMore">
		<div class="race-preview">
			<v-img
				class="race-preview-img"
				:src="mapImageSource"
				contain
				alt="race preview"
			>
			</v-img>
			<v-chip
				color="accent"
				dark
				v-if="race.price > 0 && !race.inscribed"
				class="race-preview-price"
				>{{ race.price / 100 }}€</v-chip
			>

			<v-chip
				color="accent"
				dark
				v-else-if="race.price === 0 && !race.inscribed"
				class="race-preview-price"
				>GRATIS</v-chip
			>

			<v-chip
				color="accent"
				dark
				v-else-if="race.inscribed"
				class="race-preview-price"
				>INSCRITO</v-chip
			>
		</div>
		<div class="race-description">
			<h2>
				{{ truncatedRaceName }}
			</h2>
			<h3 class="accent--text">
				{{ inscriptionCount }} usuarios inscritos
			</h3>
			<p class="race-description-text">{{ truncatedRaceDescription }}</p>
			<p class="distance">
				Distancia total:{{
					Math.floor(Math.round(race.totalDistance / 10) * 10) / 1000
				}}km
			</p>
			<div class="tags my-2">
				<h4>Categorías</h4>
				<v-chip
					v-for="tag in race.tags"
					:key="tag"
					dark
					color="secondary"
					>{{ tag }}</v-chip
				>
			</div>
			<div class="dates my-2">
				<h4>Fechas</h4>
				<v-chip color="accent">{{
					race.startDate | formatDate
				}}</v-chip>
				<v-icon color="accent" class="date-arrow-icon"
					>mdi-arrow-right</v-icon
				>
				<v-chip color="accent">{{
					race.finishDate | formatDate
				}}</v-chip>
			</div>
		</div>
		<div class="actions">
			<v-spacer></v-spacer>
			<v-btn
				elevation="0"
				large
				class="rounded-lg"
				color="info"
				dark
				:to="{
					name: 'RaceInscriptions',
					params: {
						race: race,
					},
				}"
			>
				Inscripciones
			</v-btn>
			<v-row class="ma-0 pa-4">
				<v-dialog v-model="dialog" width="600">
					<template v-slot:activator="{ on, attrs }">
						<v-btn
							elevation="0"
							large
							class="rounded-lg"
							color="info"
							dark
							v-bind="attrs"
							v-on="on"
						>
							Editar
						</v-btn>
					</template>

					<edit-race
						:race="race"
						@close="dialog = false"
						@update="$emit('updated-race')"
					></edit-race>
				</v-dialog>
			</v-row>
		</div>
	</v-card>
</template>

<script>
import moment from "moment";
import axios from "axios";

import EditRace from "./EditRace";

export default {
	components: {
		EditRace,
	},
	props: {
		race: Object,
	},
	data() {
		return {
			dialog: false,
			inscriptionCount: 0,
		};
	},
	computed: {
		mapImageSource() {
			return `${process.env.VUE_APP_BACK_URL}/media/races/previews/${this.race.id}.png`;
		},
		truncatedRaceDescription() {
			const length = 200;
			if (this.race.description) {
				//Reduce the description to 200ch
				let truncatedDescription = this.race.description.substring(
					0,
					length
				);

				//If the description was longer than 200ch append ...
				if (this.race.description.length >= length) {
					truncatedDescription += "...";
				}

				return truncatedDescription;
			} else {
				return "";
			}
		},
		truncatedRaceName() {
			const length = 50;
			if (this.race.name) {
				//Reduce the description to length
				let truncatedName = this.race.name.substring(0, length);

				//If the description was longer than length append ...
				if (this.race.name.length >= length) {
					truncatedName += "...";
				}

				return truncatedName;
			} else {
				return "";
			}
		},
	},
	filters: {
		formatDate(value) {
			return moment(value).locale("es").format("D MMM YYYY");
		},
	},
	methods: {
		showMore() {
			this.$router.push({
				name: "RaceDetails",
				query: { id: this.race.id },
			});
		},
	},
	async mounted() {
		const response = await axios.get(
			`/race/inscription-count/${this.race.id}`
		);

		this.inscriptionCount = response.data.inscriptionCount;
	},
};
</script>

<style lang="scss" scoped>
@import "../../scss/_variables.scss";
@import "../../scss/_mixins.scss";

.race-element {
	display: flex;
	flex-direction: column;
	flex-wrap: wrap;
	align-items: flex-start;
	overflow: hidden;
	min-width: 250px;
	max-width: 350px;

	@include desktop() {
		flex-direction: row;
		padding: 2em;
		max-width: initial;
		border: 1px solid #ccc;
		box-shadow: none !important;

		&:hover {
			background-color: #f3f3f4;
		}
	}

	.race-preview {
		border-radius: 0 !important;
		flex: 1;
		width: 100%;
		position: relative;

		@include desktop() {
			height: 100%;
			flex: 1;
			min-width: 200px;
			max-width: 375px;

			display: flex;
			flex-direction: column;
		}

		.race-preview-img {
			width: 100%;
		}

		@include desktop() {
			height: 100%;
			width: auto;
			align-self: center;
		}

		.race-preview-price {
			position: absolute;
			bottom: -0.75rem;
			right: 0.5em;
			font-weight: bold;

			@include desktop() {
				bottom: 0;
			}
		}
	}

	.race-description {
		padding: 0 1em 0.5em 1em;
		flex: 4;
		display: flex;
		flex-direction: column;

		@include desktop() {
			padding: 0;
			margin-left: 2em;
			flex: 2;
		}

		h2 {
			vertical-align: text-bottom;

			@include desktop() {
				min-height: 2em;
			}

			.small {
				font-weight: 200;
				font-size: 0.75em;
			}
		}

		.race-description-text {
			display: none;
			text-align: justify;

			@include desktop() {
				display: block;
				min-height: 2em;
			}
		}

		p {
			margin-top: 0.5em;
			font-size: 0.9em;
			line-height: 1.5em;
			text-align: justify;
		}

		.distance {
			font-size: 0.75em;
			color: #888;
			margin: 0;
		}

		.dates {
			span {
				font-size: 0.75em;
			}

			.date-arrow-icon {
				font-size: 1em;
			}
		}
	}

	.actions {
		display: flex;
		flex-direction: column;
		flex-wrap: wrap;
		justify-content: flex-end;
		margin-top: auto;
		align-self: flex-end;
		margin-bottom: 1em;
		align-items: center;
		padding: 0 1em;

		.show-more-column {
			display: flex;
			flex-direction: column;
		}
	}
}
</style>
